<template>
  <v-container>
    <!-- Header / Actions -->
    <v-card class="mb-3">
      <v-toolbar flat dense :color="$vuetify.theme.dark ? 'blue-grey darken-4' : 'blue-grey lighten-5'">
        <v-toolbar-title class="text-subtitle-2">Promotions</v-toolbar-title>
        <v-spacer />
        <v-btn small :loading="loading" icon @click="fetchPromotions">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
        <v-btn color="primary" small class="ml-2" @click="$router.push('/promotions/create')">
          <v-icon left small>mdi-plus</v-icon>
          New Promotion
        </v-btn>
      </v-toolbar>

      <!-- Filter bar -->
      <v-card-text class="py-2">
        <v-row dense>
          <v-col cols="12" sm="4" md="3">
            <v-text-field v-model="filters.search" dense clearable hide-details="auto" prepend-inner-icon="mdi-magnify"
              label="Search code / type" />
          </v-col>

          <v-col cols="12" sm="4" md="3">
            <v-select v-model="filters.type" :items="typeOptions" dense clearable hide-details="auto"
              prepend-inner-icon="mdi-tag" label="Type" />
          </v-col>

          <v-col cols="12" sm="4" md="3">
            <v-select v-model="filters.status" :items="statusOptions" dense clearable hide-details="auto"
              prepend-inner-icon="mdi-toggle-switch" label="Status" />
          </v-col>

          <v-col cols="12" md="3" class="d-flex align-center">
            <div class="d-flex flex-wrap gap-8">
              <v-chip small label :outlined="!isPreset('active')" @click="setPreset('active')">
                Active
              </v-chip>
              <v-chip small label :outlined="!isPreset('upcoming')" @click="setPreset('upcoming')">
                Upcoming
              </v-chip>
              <v-chip small label :outlined="!isPreset('ended')" @click="setPreset('ended')">
                Ended
              </v-chip>
              <v-chip small label color="red" text-color="white" @click="clearFilters">
                Clear
              </v-chip>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Table -->
    <v-card>
      <v-data-table class="elevation-0 promo-table" dense :headers="headers" :items="filteredPromotions"
        :loading="loading" loading-text="Loading promotions…" item-key="id"
        :footer-props="{ 'items-per-page-options': [5, 10, 15, 20, 50] }">
        <!-- Loading skeleton -->
        <template v-slot:loading>
          <v-skeleton-loader type="table" class="mx-2 my-2" />
        </template>

        <!-- Empty state -->
        <template v-slot:no-data>
          <div class="pa-6 text-center">
            <v-icon x-large class="mb-2">mdi-tag</v-icon>
            <div class="mb-1 font-weight-medium">No promotions found</div>
            <div class="text-caption grey--text">Try clearing filters or changing the search</div>
          </div>
        </template>

        <!-- Custom cells -->
        <template v-slot:item.id="{ value }">
          <code class="mono">#{{ value }}</code>
        </template>

        <template v-slot:item.code="{ value }">
          <v-chip small label class="mono">{{ value }}</v-chip>
        </template>

        <template v-slot:item.promotion_type="{ value }">
          <v-chip small :color="typeColor(value)" text-color="white" label>{{ value }}</v-chip>
        </template>

        <template v-slot:item.start_date="{ value }">
          {{ formatDate(value ? new Date(value) : null, 'en-US') }}
        </template>

        <template v-slot:item.end_date="{ value }">
          {{ formatDate(value ? new Date(value) : null, 'en-US') }}
          <v-chip v-if="isEndingSoon(value)" x-small class="ml-1" color="orange" text-color="white" label>
            Ending soon
          </v-chip>
        </template>

        <template v-slot:item.is_active="{ item }">
          <v-chip small :color="item.is_active ? 'green' : 'grey'" dark label>
            {{ item.is_active ? 'Active' : 'Inactive' }}
          </v-chip>
        </template>

        <template v-slot:item.actions="{ item }">
          <div style="display: flex;">
            <v-tooltip bottom>
              <template #activator="{ on, attrs }">
                <v-btn color="yellow darken-3" text icon v-bind="attrs" v-on="on"
                  @click="$router.push(`/promotions/${item.id}/edit`)">
                  <v-icon>mdi-pencil</v-icon>
                </v-btn>
              </template>
              <span>Edit</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template #activator="{ on, attrs }">
                <v-btn text icon @click="confirmDelete(item.id)" v-bind="attrs" v-on="on">
                  <v-icon color="red">mdi-delete</v-icon>
                </v-btn>
              </template>
              <span>Delete</span>
            </v-tooltip>
          </div>
        </template>

        <!-- Header right area (optional totals) -->
        <template v-slot:top>
          <v-alert border="left" colored-border type="info" elevation="0" class="ma-2"
            v-if="promotions && promotions.length">
            <strong>{{ filteredPromotions.length }}</strong> shown •
            <strong>{{ activeCount }}</strong> active
          </v-alert>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<script>
import axios from '@/extra-functions/axios.extra'
import { formatDate } from '../../../../lib/utils'

export default {
  name: 'PromotionIndex',
  data() {
    return {
      promotions: [],
      loading: false,
      filters: {
        search: null,
        type: null,
        status: null, // 'active' | 'inactive' | null
        preset: null, // 'active' | 'upcoming' | 'ended' | null
      },
      typeOptions: [
        // keep in sync with your backend enum
        'b1g1', 'b1gx', 'x4y', 'cross_freebie', 'pct_disc', 'fix_disc',
      ],
      statusOptions: [
        { text: 'Active', value: 'active' },
        { text: 'Inactive', value: 'inactive' },
      ],
      headers: [
        { text: '#', value: 'id', sortable: true, width: 70 },
        { text: 'Code', value: 'code' },
        { text: 'Type', value: 'promotion_type' },
        { text: 'Start Date', value: 'start_date' },
        { text: 'End Date', value: 'end_date' },
        { text: 'Status', value: 'is_active' },
        { text: 'Actions', value: 'actions', sortable: false, width: 110 },
      ],
    }
  },
  created() {
    this.fetchPromotions()
  },
  computed: {
    filteredPromotions() {
      const s = (this.filters.search || '').toLowerCase().trim()
      const now = new Date()

      return (this.promotions || [])
        .filter(p => {
          // text search on code + type
          const passSearch =
            !s ||
            (p.code || '').toLowerCase().includes(s) ||
            (p.promotion_type || '').toLowerCase().includes(s)

          // status filter
          const passStatus =
            !this.filters.status ||
            (this.filters.status === 'active' && p.is_active) ||
            (this.filters.status === 'inactive' && !p.is_active)

          // type filter
          const passType = !this.filters.type || p.promotion_type === this.filters.type

          // preset filter (active/upcoming/ended based on dates)
          const sd = p.start_date ? new Date(p.start_date) : null
          const ed = p.end_date ? new Date(p.end_date) : null
          let passPreset = true
          if (this.filters.preset === 'active') {
            passPreset = (!sd || sd <= now) && (!ed || ed >= now)
          } else if (this.filters.preset === 'upcoming') {
            passPreset = sd && sd > now
          } else if (this.filters.preset === 'ended') {
            passPreset = ed && ed < now
          }

          return passSearch && passStatus && passType && passPreset
        })
        .sort((a, b) => (b.is_active - a.is_active) || String(a.code).localeCompare(String(b.code)))
    },
    activeCount() {
      return (this.promotions || []).filter(p => p.is_active).length
    },
  },
  methods: {
    async fetchPromotions() {
      this.loading = true
      try {
        const { data } = await axios(this.$store.state.auth.token).get('/promotions')
        this.promotions = data
      } catch (error) {
        this.$alertSwalError('Failed to load promotions.')
      } finally {
        this.loading = false
      }
    },
    async confirmDelete(id) {
      const confirmed = await this.$confirm('Are you sure you want to delete this promotion?')
      if (!confirmed) return
      try {
        await axios(this.$store.state.auth.token).delete(`/promotions/${id}`)
        this.fetchPromotions()
      } catch (error) {
        this.$alertSwalError('Failed to delete promotion.')
      }
    },
    typeColor(type) {
      // map each type to a color that reads well on dark and light modes
      const map = {
        b1g1: 'indigo',
        b1gx: 'deep-purple',
        x4y: 'cyan',
        cross_freebie: 'teal',
        pct_disc: 'green',
        fix_disc: 'orange',
      }
      return map[type] || 'grey'
    },
    isEndingSoon(endDate) {
      if (!endDate) return false
      const now = new Date()
      const ed = new Date(endDate)
      const ms = ed - now
      const days = ms / (1000 * 60 * 60 * 24)
      return days >= 0 && days <= 3 // within 3 days
    },
    setPreset(preset) {
      this.filters.preset = preset
    },
    isPreset(preset) {
      return this.filters.preset === preset
    },
    clearFilters() {
      this.filters.search = null
      this.filters.type = null
      this.filters.status = null
      this.filters.preset = null
    },
    formatDate,
  },
}
</script>

<style lang="scss" scoped>
.promo-table ::v-deep thead th {
  position: sticky;
  top: 0;
  z-index: 2;
}

.mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace;
}

.gap-8>* {
  margin-right: .5rem;
  margin-bottom: .25rem;
}
</style>
